import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Product } from '../models/Product';
import { CartItem } from '../models/CartItem';

const CART_STORAGE_KEY = 'viviane-balas-cart';

export const WEIGHT_OPTIONS = [100, 200, 300, 400, 500] as const;
export const BASE_WEIGHT = 500;

export const calcItemPrice = (basePrice: number, weight: number): number =>
  (basePrice / BASE_WEIGHT) * weight;

interface CartContextData {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  addToCart: (product: Product, quantity: number, weight: number) => void;
  removeFromCart: (productId: number, weight: number) => void;
  updateQuantity: (productId: number, weight: number, quantity: number) => void;
  updateWeight: (productId: number, oldWeight: number, newWeight: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextData>({} as CartContextData);

const loadCart = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];
    const parsed: CartItem[] = JSON.parse(stored);
    return parsed.map((item) => ({
      ...item,
      weight: item.weight || BASE_WEIGHT,
    }));
  } catch {
    return [];
  }
};

const saveCart = (items: CartItem[]): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // silently fail
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const matchItem = (item: CartItem, productId: number, weight: number) =>
    item.product.id === productId && item.weight === weight;

  const addToCart = useCallback((product: Product, quantity: number, weight: number) => {
    setItems((prev) => {
      const existing = prev.find((item) => matchItem(item, product.id, weight));
      if (existing) {
        return prev.map((item) =>
          matchItem(item, product.id, weight)
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity, weight }];
    });
  }, []);

  const removeFromCart = useCallback((productId: number, weight: number) => {
    setItems((prev) => prev.filter((item) => !matchItem(item, productId, weight)));
  }, []);

  const updateQuantity = useCallback((productId: number, weight: number, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((item) => !matchItem(item, productId, weight)));
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        matchItem(item, productId, weight) ? { ...item, quantity } : item
      )
    );
  }, []);

  const updateWeight = useCallback((productId: number, oldWeight: number, newWeight: number) => {
    if (oldWeight === newWeight) return;
    setItems((prev) => {
      const current = prev.find((item) => matchItem(item, productId, oldWeight));
      if (!current) return prev;
      const target = prev.find((item) => matchItem(item, productId, newWeight));
      if (target) {
        return prev
          .map((item) =>
            matchItem(item, productId, newWeight)
              ? { ...item, quantity: item.quantity + current.quantity }
              : item
          )
          .filter((item) => !matchItem(item, productId, oldWeight));
      }
      return prev.map((item) =>
        matchItem(item, productId, oldWeight) ? { ...item, weight: newWeight } : item
      );
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + calcItemPrice(item.product.price || 0, item.weight) * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{ items, totalItems, totalPrice, addToCart, removeFromCart, updateQuantity, updateWeight, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextData => {
  return useContext(CartContext);
};

